import React, { useMemo } from 'react';
import { WheelPicker, WheelPickerWrapper } from '@ncdai/react-wheel-picker';

interface WheelPickerOption {
  label: string;
  value: string;
}

interface DateWheelPickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  defaultDate?: Date;
}

export const DateWheelPicker = ({ value, onChange, defaultDate }: DateWheelPickerProps) => {
  // Convert Date to individual components with stable defaults
  const fallbackDate = defaultDate || new Date();
  const selectedYear = value ? value.getFullYear().toString() : fallbackDate.getFullYear().toString();
  const selectedMonth = value ? (value.getMonth() + 1).toString() : (fallbackDate.getMonth() + 1).toString();
  const selectedDay = value ? value.getDate().toString() : fallbackDate.getDate().toString();

  // Memoize options to prevent unnecessary regeneration
  const yearOptions = useMemo((): WheelPickerOption[] => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
    return years.map(year => ({ value: year.toString(), label: year.toString() }));
  }, []);

  const monthOptions = useMemo((): WheelPickerOption[] => {
    return [
      { value: '1', label: 'Januar' },
      { value: '2', label: 'Februar' },
      { value: '3', label: 'März' },
      { value: '4', label: 'April' },
      { value: '5', label: 'Mai' },
      { value: '6', label: 'Juni' },
      { value: '7', label: 'Juli' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'Oktober' },
      { value: '11', label: 'November' },
      { value: '12', label: 'Dezember' },
    ];
  }, []);

  const dayOptions = useMemo((): WheelPickerOption[] => {
    const getDaysInMonth = (year: number, month: number) => {
      return new Date(year, month, 0).getDate();
    };

    const days = selectedYear && selectedMonth 
      ? Array.from({ length: getDaysInMonth(parseInt(selectedYear), parseInt(selectedMonth)) }, (_, i) => i + 1)
      : Array.from({ length: 31 }, (_, i) => i + 1);

    return days.map(day => ({ value: day.toString(), label: day.toString() }));
  }, [selectedYear, selectedMonth]);

  const handleDateChange = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      onChange(date);
    }
  };

  const handleYearChange = (year: string) => {
    handleDateChange(year, selectedMonth, selectedDay);
  };

  const handleMonthChange = (month: string) => {
    handleDateChange(selectedYear, month, selectedDay);
  };

  const handleDayChange = (day: string) => {
    handleDateChange(selectedYear, selectedMonth, day);
  };

  return (
    <div className="w-full rounded-lg border border-zinc-200 bg-white px-1 shadow-xs dark:border-zinc-700/80 dark:bg-zinc-900 *:data-rwp:first:*:data-rwp-highlight-wrapper:rounded-s-md *:data-rwp:last:*:data-rwp-highlight-wrapper:rounded-e-md" data-rwp-wrapper="true">
      <WheelPickerWrapper>
        <WheelPicker
          options={yearOptions}
          value={selectedYear}
          onValueChange={handleYearChange}
          infinite={false}
        />
        <WheelPicker
          options={monthOptions}
          value={selectedMonth}
          onValueChange={handleMonthChange}
          infinite={false}
        />
        <WheelPicker
          options={dayOptions}
          value={selectedDay}
          onValueChange={handleDayChange}
          infinite={false}
        />
      </WheelPickerWrapper>
    </div>
  );
};